import { Directive, ElementRef, OnInit, OnDestroy, Input } from '@angular/core';

@Directive({
  selector: 'p-editor[plainTextPaste], p-editor:not([noPlainTextPaste])'
})
export class PlainTextPasteDirective implements OnInit, OnDestroy {
  private pasteHandler?: (e: ClipboardEvent) => void;

  constructor(private el: ElementRef) {}

  ngOnInit() {
    // Wait for Quill editor to be initialized
    setTimeout(() => {
      this.attachPasteHandler();
    }, 300);
  }

  ngOnDestroy() {
    this.detachPasteHandler();
  }

  private attachPasteHandler() {
    const editorElement = this.el.nativeElement.querySelector('.ql-editor');
    if (editorElement) {
      this.pasteHandler = (e: ClipboardEvent) => {
        e.preventDefault();
        e.stopPropagation();

        // Get plain text from clipboard (this strips all formatting including background colors)
        const text = e.clipboardData?.getData('text/plain') || '';
        
        if (text) {
          // Try to find Quill instance first (preferred method)
          const quill = this.findQuillInstance();
          if (quill) {
            const quillRange = quill.getSelection(true);
            if (quillRange) {
              // Insert plain text using Quill API (removes all formatting)
              quill.insertText(quillRange.index, text, 'user');
              quill.setSelection(quillRange.index + text.length, 'user');
              return;
            }
          }
          
          // Fallback: Use Selection API for plain text insertion
          const selection = window.getSelection();
          if (selection && selection.rangeCount > 0) {
            const range = selection.getRangeAt(0);
            range.deleteContents();
            
            // Create a text node and insert it (no formatting)
            const textNode = document.createTextNode(text);
            range.insertNode(textNode);
            
            // Move cursor to end of inserted text
            range.setStartAfter(textNode);
            range.collapse(true);
            selection.removeAllRanges();
            selection.addRange(range);
          } else {
            // Last resort: append at end
            const textNode = document.createTextNode(text);
            editorElement.appendChild(textNode);
          }
        }
      };

      editorElement.addEventListener('paste', this.pasteHandler, true);
    }
  }

  private findQuillInstance(): any {
    // Try to find Quill instance using various methods
    const container = this.el.nativeElement.querySelector('.p-editor-container');
    if (container) {
      // Method 1: Quill.find() if available
      if ((window as any).Quill) {
        const quill = (window as any).Quill.find(container);
        if (quill) return quill;
      }

      // Method 2: Check for Quill attached to element
      const editorElement = container.querySelector('.ql-editor');
      if (editorElement) {
        if ((window as any).Quill) {
          const quill = (window as any).Quill.find(editorElement);
          if (quill) return quill;
        }
        // Check if Quill is stored on the element
        if ((editorElement as any).__quill) {
          return (editorElement as any).__quill;
        }
      }
    }
    return null;
  }

  private detachPasteHandler() {
    if (this.pasteHandler) {
      const editorElement = this.el.nativeElement.querySelector('.ql-editor');
      if (editorElement) {
        editorElement.removeEventListener('paste', this.pasteHandler, true);
      }
    }
  }
}

